import { type InputHTMLAttributes, useEffect, useRef } from 'react';
import { useNode, type Node, ROOT_NODE, useEditor } from '@craftjs/core';
import { cx } from '@emotion/css';
import IntlTelInput from 'intl-tel-input/react';

import type { NodeCustomProps } from '../../../types/craft';
import { useRenderContext } from '../../../context/RenderContext';
import { FORM_PHONE_FIELD_NODE_NAME } from '../../../utils/constants';
import { generateUuidV4 } from '../form-utils';
import { getBreakpointProp } from '../../../utils/breakpointUtils';
import { getHelpTextStyle } from './fieldStyles';
import { IconAlert } from './IconAlert';
import { FormPhoneFieldPropsDto } from '../../../dto/craft/props/FormPhoneFieldPropsDto';

export type FormPhoneFieldProps = FormPhoneFieldPropsDto;

// Generate a stable ID for new instances
const generateStableId = () => generateUuidV4();

export const defaultProps: FormPhoneFieldProps = {
  id: '',
  label: 'Phone Number',
  showLabel: false,
  placeholder: 'Phone',
  required: false,
  hidden: false,
  helpText: '',
  showHelpText: false,
};

const FormPhoneField = (props: Partial<FormPhoneFieldProps>) => {
  const mergedProps: FormPhoneFieldProps = { ...defaultProps, ...props };
  const { isEditable, environment } = useRenderContext();
  const isNotServerRendering = environment !== 'server';

  const {
    id: craftNodeId,
    isLocked,
    connectors: { connect, drag },
  } = useNode((node: Node) => ({
    parentId: node.data.parent as string | null,
    isLocked: node.data.custom?.isLocked,
  }));

  const { actions: editorActions, breakpoint } = useEditor((_, query) => {
    const rootNode = query.node(ROOT_NODE).get();
    return {
      breakpoint: rootNode?.data.props.breakpoint || 'desktop',
    };
  });

  // Read props using breakpoint-aware getter
  const id = getBreakpointProp(mergedProps, 'id', breakpoint);
  const label = getBreakpointProp(mergedProps, 'label', breakpoint);
  const showLabel = getBreakpointProp(mergedProps, 'showLabel', breakpoint);
  const placeholder = getBreakpointProp(mergedProps, 'placeholder', breakpoint) ?? '';
  const required = getBreakpointProp(mergedProps, 'required', breakpoint);
  const hidden = getBreakpointProp(mergedProps, 'hidden', breakpoint);
  const helpText = getBreakpointProp(mergedProps, 'helpText', breakpoint);
  const showHelpText = getBreakpointProp(mergedProps, 'showHelpText', breakpoint);
  const defaultCountry = getBreakpointProp(mergedProps, 'defaultCountry', breakpoint);

  // Generate a unique ID if one isn't set (using useEffect to avoid render-time state updates)
  const hasGeneratedId = useRef(false);
  useEffect(() => {
    if (!id && !hasGeneratedId.current) {
      hasGeneratedId.current = true;
      editorActions.history.ignore().setProp(craftNodeId, (props: FormPhoneFieldProps) => {
        props.id = generateStableId();
      });
    }
  }, [id, editorActions, craftNodeId]);

  const disablePointerEvents = isEditable && isLocked;
  const inputTabIndex = isEditable ? (isLocked ? -1 : 0) : 0;

  // Use persisted ID from props, fallback to temp during first render before useEffect runs
  const currentId = id || 'temp-phone-field';

  const inputProps = buildInputProps(
    currentId,
    placeholder,
    required,
    disablePointerEvents,
    inputTabIndex,
    `${currentId}-helper-text`,
  );

  return (
    <div
      ref={(ref) => {
        if (ref) connect(drag(ref));
      }}
      className={cx(
        'field-node',
        'field-node--phone',
        isEditable && 'node-dashed-indicator styled-node',
        'boundary-indicator',
        disablePointerEvents && 'pointer-events-none',
        hidden && 'field-node--hidden',
      )}
      data-field-id={currentId}
      aria-hidden={hidden || undefined}
    >
      {showLabel && (
        <label className="field-label" htmlFor={currentId}>
          {label}
          {required ? (
            <span aria-hidden="true" className="field-required-indicator">
              *
            </span>
          ) : null}
        </label>
      )}
      <div className="field-input-wrapper field-input-wrapper--phone">
        <div key={defaultCountry || 'default'} style={{ width: '100%' }}>
          {isNotServerRendering ? (
            <IntlTelInput
              initOptions={{
                separateDialCode: true,
                showFlags: true,
                useFullscreenPopup: false,
                autoPlaceholder: 'off',
                placeholderNumberType: 'MOBILE',
                initialCountry: (defaultCountry || 'auto') as 'auto',
                countrySearch: true,
                nationalMode: true,
                formatOnDisplay: true,
                formatAsYouType: true,
                strictMode: true,
                fixDropdownWidth: false,
                loadUtils: () => import('intl-tel-input/utils'),
                i18n: {
                  searchPlaceholder: 'Search...',
                },
              }}
              inputProps={inputProps}
            />
          ) : (
            <input
              {...inputProps}
              className="field-input"
              type="tel"
              required={required}
              data-default-country={defaultCountry || 'auto'}
            />
          )}
        </div>
        <span className="field-input-icon" aria-hidden="true">
          <IconAlert />
        </span>
      </div>
      <div
        className={cx(
          'field-helper-text-wrapper',
          getHelpTextStyle({ showHelpText, helpText } as FormPhoneFieldProps),
        )}
      >
        <p id={`${currentId}-helper-text`} className="field-helper-text">
          {showHelpText && helpText && helpText.trim().length > 0 ? helpText : '\u200B'}
        </p>
      </div>
    </div>
  );
};

FormPhoneField.craft = {
  displayName: FORM_PHONE_FIELD_NODE_NAME,
  props: defaultProps,
  custom: {
    displayName: 'Phone Field',
    isLocked: false,
  } as NodeCustomProps,
};

export default FormPhoneField;

function buildInputProps(
  id: string,
  placeholder: string,
  required: boolean,
  disablePointerEvents: boolean,
  tabIndex: number,
  describedById?: string,
): InputHTMLAttributes<HTMLInputElement> {
  const attributes: InputHTMLAttributes<HTMLInputElement> = {
    id,
    name: id,
    type: 'tel',
    className: 'field-input',
    placeholder: placeholder || undefined,
    required,
    readOnly: disablePointerEvents,
    tabIndex,
    'aria-required': required,
    'aria-describedby': describedById,
    autoComplete: 'off', // Disable browser autocomplete popup
  };

  (attributes as Record<string, unknown>)['data-required'] = required || undefined;

  return attributes;
}
